import { serializeElementsToFountain } from '@/features/studio/script/fountain-serialize'
import { parseFountainToElements } from '@/features/studio/script/fountain-parse'
import { deriveScenesFromBody } from '@/features/studio/script/derive-scenes'
import type { ScriptElement, ScriptElementType } from '@/features/studio/script/script-elements'
import type { ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'

import type { ScriptScene } from '@/api/studio-production'

export type ScriptAISelection = {
  start: number
  end: number
  text: string
  elementType?: ScriptElementType
  elementContent?: string
}

/** 按 serializeElementsToFountain 规则计算每个元素在 body 中的 [start, end) 区间。 */
export function mapElementBodyRanges(
  elements: ScriptElement[],
  convention: ScriptConvention,
): Map<string, { start: number; end: number }> {
  const ranges = new Map<string, { start: number; end: number }>()
  let offset = 0

  for (let i = 0; i < elements.length; i += 1) {
    const prefix = serializeElementsToFountain(elements.slice(0, i), convention)
    const through = serializeElementsToFountain(elements.slice(0, i + 1), convention)
    const start = prefix.length
    const end = through.length
    ranges.set(elements[i].id, { start, end })
    offset = end
  }

  void offset
  return ranges
}

export function resolveScriptAISelection(input: {
  body: string
  convention: ScriptConvention
  activeElementId?: string | null
  textarea?: HTMLTextAreaElement | null
  fallbackStart?: number
  fallbackEnd?: number
}): ScriptAISelection | null {
  const { body, convention, activeElementId, textarea, fallbackStart, fallbackEnd } = input

  if (textarea && fallbackStart != null && fallbackEnd != null) {
    const start = Math.max(0, Math.min(fallbackStart, body.length))
    const end = Math.max(start, Math.min(fallbackEnd, body.length))
    return { start, end, text: body.slice(start, end) }
  }

  if (!activeElementId) return null

  const elements = parseFountainToElements(body, convention)
  const el = elements.find((e) => e.id === activeElementId)
  if (!el) return null

  const ranges = mapElementBodyRanges(elements, convention)
  const range = ranges.get(activeElementId)
  if (!range) return null

  let start = range.start
  let end = range.end

  if (textarea) {
    const localStart = textarea.selectionStart ?? 0
    const localEnd = textarea.selectionEnd ?? localStart
    if (localStart !== localEnd) {
      const serialized = serializeElementsToFountain([el], convention)
      const contentStart = serialized.indexOf(el.content)
      if (contentStart >= 0) {
        const contentOffset = contentStart
        start = range.start + contentOffset + localStart
        end = range.start + contentOffset + localEnd
      }
    }
  }

  start = Math.max(0, Math.min(start, body.length))
  end = Math.max(start, Math.min(end, body.length))

  return {
    start,
    end,
    text: body.slice(start, end),
    elementType: el.type,
    elementContent: el.content,
  }
}

export function mergeBodyAtSelection(
  body: string,
  replacement: string,
  start: number,
  end: number,
): string {
  const s = Math.max(0, Math.min(start, body.length))
  const e = Math.max(s, Math.min(end, body.length))
  return body.slice(0, s) + replacement + body.slice(e)
}

/** 在 body 中定位指定场次的文本区间（用于场级 AI / 删除场）。 */
export function findSceneRangeInBody(
  body: string,
  scenes: ScriptScene[],
  sceneNo: number,
  convention: ScriptConvention = 'cn',
): ScriptAISelection | null {
  if (!scenes.some((s) => s.scene_no === sceneNo)) return null

  const elements = parseFountainToElements(body, convention)
  const ranges = mapElementBodyRanges(elements, convention)
  const headingEl = elements.find((e) => e.type === 'scene_heading' && e.sceneNo === sceneNo)
  if (!headingEl) return null

  const headingRange = ranges.get(headingEl.id)
  if (!headingRange) return null

  const sorted = [...deriveScenesFromBody(body, convention)].sort((a, b) => a.scene_no - b.scene_no)
  const idx = sorted.findIndex((s) => s.scene_no === sceneNo)
  let end = body.length
  const nextSceneNo = sorted[idx + 1]?.scene_no
  if (nextSceneNo != null) {
    const nextHeadingEl = elements.find(
      (e) => e.type === 'scene_heading' && e.sceneNo === nextSceneNo,
    )
    const nextRange = nextHeadingEl ? ranges.get(nextHeadingEl.id) : null
    if (nextRange) end = nextRange.start
  }

  const start = headingRange.start
  return { start, end, text: body.slice(start, end) }
}
